import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  EyeIcon,
  EyeOffIcon,
  ListIndentIncreaseIcon,
  MoreHorizontalIcon,
  RotateCcwIcon,
  Share2Icon,
  SquareIcon,
  Trash2Icon,
} from 'lucide-react';

import {
  AiToolMenuItem,
  pickUpTaskAsk,
  useAiToolHelp,
} from '@/features/editor-access';
import { useRelatedTaskLink } from '@/features/task-relations';
import { useCanWork } from '@/features/workspaces/canWork';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTaskCommands } from '../hooks/useTaskCommands';
import { STOP_WAITING } from '../linkedWaiting';
import { HIDE_WHILE_RUNNING_REASON, useHideTask } from '../hooks/useHideTask';
import { usePendingTaskAction } from '../hooks/usePendingTaskActions';
import type { TaskView } from '@/lib/api/generated/model';
import { useDeletionImpact } from '../hooks/useDeletionImpact';
import { useHoldWhileOpen } from '../interactionHold';
import { DeleteTaskDialog } from './DeleteTaskDialog';
import { MoveToSheet } from './MoveToSheet';
import { useBoardMove } from '../boardStepsContext';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

/**
 * A task's actions menu. Owns the related-tasks dialog's `open` boolean so
 * each card/drawer instance has its own dialog, following `WorkflowRowMenu`'s
 * pattern for the same relationship (menu owns the dialog it opens).
 */
export function TaskActionsMenu({
  workspaceId,
  taskId,
  taskTitle,
  workflowId,
  isRunning = false,
  isHidden = false,
  canRunAgain = true,
  atGate = false,
  task: taskView,
  onDeleted,
  waitingOnChildren = false,
  offerAiTool = false,
}: {
  workspaceId: string;
  taskId: string;
  taskTitle: string;
  /** The task's workflow, needed to address its Relations tab. */
  workflowId: string;
  /** Whether a run is in flight, which decides which actions are offered (078). */
  isRunning?: boolean;
  isHidden?: boolean;
  /**
   * Whether "Run again" would repeat work (`canRunAgain`, spec 249). It repeats
   * the most recent run (078 US2): a task that has none has nothing to repeat,
   * and one whose latest run is Backlog's, Done's or a gate's would only gain
   * another pass-through line. Neither is offered the action.
   */
  canRunAgain?: boolean;
  /**
   * Whether the task stands on a human gate (spec 172). Its next move is a
   * person's decision, which the gate's outcomes offer; repeating the run that
   * brought it there is not one of them, so Run again is not offered.
   */
  atGate?: boolean;
  /**
   * The task itself, so the delete confirmation can say how many subtasks go
   * with it. Without it the confirmation names the task alone.
   */
  task?: TaskView;
  /** Called once the task is gone, so a drawer showing it can close. */
  onDeleted?: () => void;
  /**
   * Whether the run in flight is a join waiting on the tasks it created
   * (spec 140 US5). Its Stop reads "Stop waiting": it stops the waiting,
   * not them.
   */
  waitingOnChildren?: boolean;
  /**
   * Offer the ask for the person's AI tool (spec 248). The task's own view
   * offers it; a board card does not, where an extra item is only noise.
   */
  offerAiTool?: boolean;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Arrivals wait while the menu is open, so the card it belongs to does not
  // move out from under it (spec 148 FR-002).
  const [menuOpen, setMenuOpen] = useState(false);
  // "Move to…" on a phone's board (spec 262): a move without a drag.
  const [moveOpen, setMoveOpen] = useState(false);
  const boardMove = useBoardMove();
  const isPhone = usePhoneWidth();
  const offerMove = isPhone && boardMove !== null && taskView !== undefined;
  useHoldWhileOpen(menuOpen);
  const deletion = useDeletionImpact(workspaceId, taskView);
  // Where this task's connections live. Null when the task's workflow cannot be
  // resolved, in which case the menu simply does not offer the item.
  const relationsLink = useRelatedTaskLink(workspaceId)(workflowId, taskId);
  // Every action goes through the one place that confirms it (spec 125
  // FR-009, FR-011): the toast survives the menu closing, which an inline
  // message here could not, and it names the task the card is about.
  const commands = useTaskCommands(workspaceId);
  const { setTaskHidden } = useHideTask(workspaceId);
  const task = { id: taskId, title: taskTitle };
  // One action at a time, wherever it was started. Stopping already waits for
  // confirmation, and letting a person press Run again while that is in flight
  // would queue a run against a task the server is still bringing to a halt.
  const busy = usePendingTaskAction(taskId) !== undefined;
  const canWork = useCanWork();
  const aiHelp = useAiToolHelp();
  const aiItem = offerAiTool && aiHelp !== 'hidden';

  // Offered by state rather than always-shown-and-refused. Each of these would fail with
  // a 409 in the wrong state, and a menu item whose only outcome is an error teaches
  // people to distrust the menu.

  if (!canWork && !relationsLink && !aiItem) return null;

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          setMenuOpen(open);
        }}
      >
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions for ${taskTitle}`}
            />
          }
        >
          <MoreHorizontalIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          {/*
            A link to the task's own Relations tab, not a window of its own.
            This used to open a read-only dialog that showed a task's whole
            neighbourhood and could change none of it, while the only way to add
            or remove a connection sat below the fold of the Details tab. One
            home now, and this is a door to it (spec 100 FR-007, FR-008).

            Rendered as a link so it behaves like one: middle-click and
            modifier-click open a new browser tab because the person asked, and
            the address is visible before the click.
          */}
          {relationsLink ? (
            <DropdownMenuItem render={<Link {...relationsLink} />}>
              <Share2Icon aria-hidden="true" />
              Relations
            </DropdownMenuItem>
          ) : null}

          {/* It only reads, so someone only reading the workspace gets it too. */}
          {offerAiTool && aiHelp !== 'hidden' ? (
            <AiToolMenuItem
              help={aiHelp}
              ask={pickUpTaskAsk({ id: taskId, title: taskTitle })}
            />
          ) : null}

          {/* Someone only reading the workspace keeps the way to the task's
              relations and nothing that changes it (spec 189). */}
          {canWork ? (
            <>
              <DropdownMenuSeparator />

              {offerMove ? (
                <DropdownMenuItem
                  disabled={busy}
                  onClick={() => {
                    setMoveOpen(true);
                  }}
                >
                  <ListIndentIncreaseIcon aria-hidden="true" />
                  Move to…
                </DropdownMenuItem>
              ) : null}

              {isRunning ? (
                <DropdownMenuItem
                  disabled={busy}
                  onClick={() => {
                    commands.stop(task, { waitingOnChildren });
                  }}
                >
                  <SquareIcon aria-hidden="true" />
                  {waitingOnChildren ? STOP_WAITING : 'Stop'}
                </DropdownMenuItem>
              ) : canRunAgain && !atGate ? (
                <DropdownMenuItem
                  disabled={busy}
                  onClick={() => {
                    commands.runAgain(task);
                  }}
                >
                  <RotateCcwIcon aria-hidden="true" />
                  Run again
                </DropdownMenuItem>
              ) : null}

              {/*
            A running task cannot be hidden — it would go on spending with
            nothing on the board to explain it. The reason is a visible line
            in the item rather than a tooltip, because a disabled menu item
            takes no focus and a tooltip there would never be read (FR-012).
          */}
              <DropdownMenuItem
                disabled={busy || (!isHidden && isRunning)}
                onClick={() => {
                  setTaskHidden(task, !isHidden);
                }}
              >
                {isHidden ? (
                  <EyeIcon aria-hidden="true" />
                ) : (
                  <EyeOffIcon aria-hidden="true" />
                )}
                <span className="flex flex-col">
                  {isHidden ? 'Show on the board' : 'Hide from the board'}
                  {!isHidden && isRunning ? (
                    <span className="text-muted-foreground text-xs">
                      {HIDE_WHILE_RUNNING_REASON}
                    </span>
                  ) : null}
                </span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  setDeleteOpen(true);
                }}
              >
                <Trash2Icon aria-hidden="true" />
                Delete
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {offerMove ? (
        <MoveToSheet
          task={taskView}
          open={moveOpen}
          onOpenChange={setMoveOpen}
        />
      ) : null}

      <DeleteTaskDialog
        workspaceId={workspaceId}
        taskId={taskId}
        taskTitle={taskTitle}
        impact={deletion ?? { count: 0, anyRunning: isRunning }}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onDeleted}
      />
    </>
  );
}
